// Keywords: what the sampled apps rank for, with the provider's demand
// metrics, the trend against the previous snapshot, and semantic clusters.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collectRunKeywords, getRunKeywords, type RunKeyword } from '@/api/aso';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';

const fmt = (sec: number | null | undefined) => (sec ? new Date(sec * 1000).toLocaleString() : '—');
const n = (v: number | null) => (v === null || v === undefined ? '—' : Number.isInteger(v) ? String(v) : v.toFixed(1));

const VIEWS = [
  { value: 'top', label: 'Top keywords — by traffic' },
  { value: 'competitor', label: 'Competitor keywords — most apps ranking' },
  { value: 'growing', label: 'Growing — vs previous snapshot' },
  { value: 'easy', label: 'Effective — high KEI' },
];

export function useRunKeywords(runId: number) {
  return useQuery({
    queryKey: ['aso-keywords', runId],
    queryFn: () => getRunKeywords(runId),
    enabled: runId > 0,
    refetchInterval: (q) => (q.state.data?.run.keywords === 'queued' ? 15_000 : false),
  });
}

export function KeywordsTab({ runId }: { runId: number }) {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const q = useRunKeywords(runId);
  const [view, setView] = useState('top');
  const [cluster, setCluster] = useState('');
  const [needle, setNeedle] = useState('');
  const [open, setOpen] = useState<RunKeyword | null>(null);
  const data = q.data;

  const collect = useMutation({
    mutationFn: () => collectRunKeywords(runId),
    onSuccess: (r) => { toast(`Keyword collection queued for ${r.queued} app(s)`); qc.invalidateQueries({ queryKey: ['aso-keywords', runId] }); },
    onError: (e: any) => toast(e?.message ?? 'Failed to queue keyword collection'),
  });

  const clusters = useMemo(() => {
    const used = new Set((data?.keywords ?? []).map((k) => k.cluster_slug).filter((c): c is string => !!c));
    return [{ value: '', label: 'All clusters' }, ...[...used].sort().map((c) => ({ value: c, label: data?.labels.cluster[c] ?? c }))];
  }, [data]);

  const rows = useMemo(() => {
    let list = data?.keywords ?? [];
    if (cluster) list = list.filter((k) => k.cluster_slug === cluster);
    const s = needle.trim().toLowerCase();
    if (s) list = list.filter((k) => k.keyword.includes(s));
    const growth = (k: RunKeyword) => (k.previous?.traffic != null && k.traffic != null ? k.traffic - k.previous.traffic : null);
    switch (view) {
      case 'competitor': return [...list].sort((a, b) => b.apps_count - a.apps_count || (b.traffic ?? 0) - (a.traffic ?? 0));
      case 'growing': return list.filter((k) => growth(k) !== null && growth(k)! > 0).sort((a, b) => growth(b)! - growth(a)!);
      case 'easy': return list.filter((k) => k.kei != null).sort((a, b) => (b.kei ?? 0) - (a.kei ?? 0));
      default: return list;
    }
  }, [data, view, cluster, needle]);

  const run = data?.run;
  return (
    <div className="space-y-3">
      {run && (
        <div className="flex flex-wrap items-center gap-3 text-xs text-content-muted">
          {run.keywords === 'none' && <span>Keywords not collected for this snapshot.</span>}
          {run.keywords === 'queued' && <span className="text-primary">Collecting… {run.pending} app(s) left</span>}
          {run.keywords === 'done' && <span>Collected {fmt(run.keywords_at)} · {data?.keywords.length ?? 0} keywords{run.previous_run_id ? ' · trend vs previous snapshot' : ''}</span>}
          {run.keywords === 'failed' && <span className="text-danger">Keyword collection failed</span>}
          {run.keywords_error && <span className="text-warning" title={run.keywords_error}>{run.keywords_error}</span>}
          {canEdit && run.keywords !== 'queued' && (
            <span className="ml-auto">
              <Button size="sm" variant="secondary" disabled={collect.isPending} onClick={() => collect.mutate()}>
                {run.keywords === 'done' ? 'Re-collect keywords' : 'Collect keywords'}
              </Button>
            </span>
          )}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={view} options={VIEWS} onChange={setView} className="sm:w-72" />
        <Select value={cluster} options={clusters} onChange={setCluster} className="sm:w-60" />
        <input className="input sm:max-w-xs" placeholder="Find a keyword…" value={needle} onChange={(e) => setNeedle(e.target.value)} />
      </div>
      <div className="bg-surface border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-surface-muted text-xs uppercase tracking-wide text-content-subtle">
            <tr>
              <th className="text-left p-3 font-medium">Keyword</th>
              <th className="text-left p-3 font-medium">Cluster</th>
              <th className="text-right p-3 font-medium" title="Provider's search-volume score">Traffic</th>
              <th className="text-right p-3 font-medium" title="Competition index — lower is easier">Difficulty</th>
              <th className="text-right p-3 font-medium" title="Effectiveness: traffic vs competition">KEI</th>
              <th className="text-right p-3 font-medium">Apps ranking</th>
              <th className="text-right p-3 font-medium">Best rank</th>
              <th className="text-right p-3 font-medium">Trend</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-content-subtle">{run?.keywords === 'done' ? 'Nothing matches.' : 'No keywords yet.'}</td></tr>}
            {rows.slice(0, 300).map((k) => {
              const delta = k.previous?.traffic != null && k.traffic != null ? k.traffic - k.previous.traffic : null;
              return (
                <tr key={k.keyword_id} className="border-t border-border hover:bg-surface-muted/40 cursor-pointer" onClick={() => setOpen(k)}>
                  <td className="p-3 align-middle text-content font-medium">{k.keyword}</td>
                  <td className="p-3 align-middle text-xs text-content-muted">{k.cluster_slug ? (data?.labels.cluster[k.cluster_slug] ?? k.cluster_slug) : '—'}</td>
                  <td className="p-3 align-middle text-right text-content">{n(k.traffic)}</td>
                  <td className="p-3 align-middle text-right text-content-muted">{n(k.difficulty)}</td>
                  <td className="p-3 align-middle text-right text-content-muted">{n(k.kei)}</td>
                  <td className="p-3 align-middle text-right text-content">{k.apps_count}</td>
                  <td className="p-3 align-middle text-right text-content-muted">{k.best_rank ?? '—'}</td>
                  <td className={`p-3 align-middle text-right text-xs ${delta === null ? 'text-content-subtle' : delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-content-muted'}`}>
                    {delta === null ? (k.previous ? '0' : 'new') : `${delta > 0 ? '+' : ''}${n(delta)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {open && (
        <Modal isOpen onClose={() => setOpen(null)} title={`“${open.keyword}”`} size="lg">
          <div className="text-xs text-content-muted mb-3">
            traffic {n(open.traffic)} · difficulty {n(open.difficulty)} · KEI {n(open.kei)} · {open.provider}
            {open.cluster_slug ? ` · ${data?.labels.cluster[open.cluster_slug] ?? open.cluster_slug}` : ''}
          </div>
          <div className="label mb-1">Apps ranking for it ({open.apps.length})</div>
          <ul className="divide-y divide-border">
            {open.apps.map((a) => (
              <li key={a.app_id} className="py-1.5 text-sm flex justify-between"><span className="text-content">{a.name}</span><span className="text-content-muted">#{a.rank ?? '—'}</span></li>
            ))}
          </ul>
        </Modal>
      )}
    </div>
  );
}
